/**
 * useDocumentBlobUrl - the auth-gated "blob detour" behind an inline document preview.
 *
 * live mode → GET /documents/:id/content, wrapped in an object URL an iframe can read.
 * mock mode → `mockUrl` verbatim; no request, no bytes, no auth needed.
 *
 * An `<iframe src="/api/documents/:id/content">` sends no Authorization header, so the
 * bytes are fetched with the bearer first. `/content` carries `document.read`; the
 * separate `/download` route carries `document.download`, so previewing never grants a
 * download.
 *
 * The Blob is server state and lives in TanStack Query (no hand-rolled loading flags),
 * which also caches it - reopening an attachment refetches nothing, and each fetch
 * buffers the whole file. The object URL is a browser handle, not server state, so one
 * effect creates and revokes it.
 *
 * RAW read: it returns the bytes whatever they are and verifies nothing. `blobType`
 * carries the type the server sent, and anything that RENDERS the result must check it
 * first - an object URL inherits our origin, so unverified bytes execute as our page.
 *
 * A document the mail-team gate hides is refused as an ordinary 404, never a 403 ("a
 * 403 confirms the document exists"), so hidden and missing are indistinguishable by
 * design and callers must word failure to cover both.
 *
 * [Source: apps/web/src/features/documents/hooks/useDocumentDetail.ts] (hook shape)
 * [Source: apps/web/src/lib/api-client.ts] (apiFetchBlob / documents.content)
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';

export interface DocumentBlobUrl {
  /** The iframe `src`, or null while loading / on failure / when unavailable. */
  src: string | null;
  /** True while the live fetch is in flight. */
  isLoading: boolean;
  /** True when the bytes could not be fetched - missing, unavailable, or gated. */
  isError: boolean;
  /** The blob's real Content-Type; null in mock mode or before the bytes arrive. */
  blobType: string | null;
}

/** `id` null fetches nothing - how a caller skips a file it could not render anyway. */
export function useDocumentBlobUrl(id: string | null, mockUrl?: string | null): DocumentBlobUrl {
  const live = isLiveData();
  const enabled = live && id !== null;

  const query = useQuery<Blob>({
    queryKey: qk.documentContent(id ?? 'none'),
    queryFn: () => apiClient.documents.content(id as string),
    enabled,
    // One version's bytes never change, and a refetch re-downloads the whole file.
    staleTime: Infinity,
    retry: false,
  });

  const blob = query.data;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Revoke on unmount or id change, or each preview leaks a whole file.
  useEffect(() => {
    if (!blob) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  if (!live) {
    return { src: mockUrl ?? null, isLoading: false, isError: false, blobType: null };
  }
  return {
    src: blobUrl,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
    blobType: blob?.type ?? null,
  };
}
